import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../../lib/supabase';

export type PointVoucherOrder = { id: string; estado: string; pago_id: string | null; monto_membresia: number; mp_payment_id: string | null; mp_order_id: string | null; idempotency_key?: string };
type ManualVoucher = { id: string; point_clave_local: string; monto: number; metodo_pago: string; banco: string | null; numero_comprobante: string | null; fecha_pago: string };
type Group = { id: string; pago_id: string | null; estado: string; monto_total: number; monto_abonado: number; saldo: number; ordenes: PointVoucherOrder[]; vouchers_manuales?: ManualVoucher[] };
type Result = { order: PointVoucherOrder | null; group?: Group | null };
const pendingStates = ['creando','created','at_terminal','action_required','pending','processing','procesando','processed','approved'];
type Options = { enabled: boolean; gymId?: string; branchId?: string; userId?: string; planId?: string; onOrder: (order: PointVoucherOrder, rowId?: string) => void; onManual: (voucher: ManualVoucher) => void };

export function useSmartPointVouchers(options: Options) {
  const { enabled, gymId, branchId, userId, planId } = options;
  const [group, setGroup] = useState<Group | null>(null);
  const [order, setOrder] = useState<PointVoucherOrder | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);
  const onOrder = useRef(options.onOrder); onOrder.current = options.onOrder;
  const onManual = useRef(options.onManual); onManual.current = options.onManual;
  const activeRow = useRef<string | undefined>(undefined);
  const inFlight = useRef(false);
  const lockedAmount = useRef<number | null>(null);
  const key = `point-membership:${gymId}:${branchId}:${userId}:${planId}`;
  const apply = useCallback((result: Result) => {
    setGroup(result.group || null);
    for (const item of result.group?.vouchers_manuales || []) onManual.current(item);
    for (const item of result.group?.ordenes || []) if (item.pago_id) onOrder.current(item, item.id === result.order?.id ? activeRow.current : undefined);
    if (result.order) onOrder.current(result.order, activeRow.current);
    const pending = result.order && !result.order.pago_id && pendingStates.includes(result.order.estado);
    setOrder(pending ? result.order : null); setBusy(Boolean(pending));
    if (pending && result.order) {
      if (result.order.idempotency_key) localStorage.setItem(key, result.order.idempotency_key);
      lockedAmount.current = Number(result.order.monto_membresia);
    }
    if (!pending) { localStorage.removeItem(key); lockedAmount.current = null; activeRow.current = undefined; }
  }, [key]);
  const invoke = useCallback(async (body: Record<string, unknown>): Promise<Result> => {
    const { data, error: failure } = await supabase.functions.invoke('mercadopago-point', { body: { ...body, gimnasio_id: gymId, sucursal_id: branchId, modo: 'production' } });
    if (failure) { const payload = await failure.context?.json?.().catch(() => null); throw Object.assign(Error(payload?.error || failure.message), { definitive: failure.context?.status >= 400 && failure.context?.status < 500 }); }
    if (!data || data.error) throw Error(data?.error || 'No se pudo consultar el cobro.');
    return data as Result;
  }, [branchId, gymId]);
  const recover = useCallback(async () => {
    if (!enabled || !userId || !planId) return;
    setBusy(true); setReady(false);
    try { apply(await invoke({ action: 'membership_state', usuario_id: userId, plan_id: planId, request_id: localStorage.getItem(key) })); setReady(true); setError(''); }
    catch (failure) { setError(failure instanceof Error ? failure.message : 'No se pudo recuperar el saldo.'); }
  }, [apply, enabled, invoke, key, planId, userId]);
  useEffect(() => { setGroup(null); setOrder(null); setBusy(false); setReady(false); if (enabled) void recover(); }, [enabled, recover]);
  const refresh = useCallback(async () => {
    if (!order || inFlight.current) return;
    inFlight.current = true;
    try { apply(await invoke({ action: 'status', order_id: order.id })); setError(''); }
    catch (failure) { setError(failure instanceof Error ? failure.message : 'No se pudo consultar el cobro.'); }
    finally { inFlight.current = false; }
  }, [apply, invoke, order]);
  useEffect(() => { if (!order) return; const timer = window.setInterval(() => void refresh(), 4000); return () => window.clearInterval(timer); }, [order, refresh]);
  const charge = async (rowId: string, amount: number, notes: string) => {
    if (inFlight.current || !ready) return;
    activeRow.current = rowId; inFlight.current = true; setBusy(true); setError('');
    let request = localStorage.getItem(key);
    if (!request) { request = crypto.randomUUID(); localStorage.setItem(key, request); }
    lockedAmount.current ??= amount;
    try { apply(await invoke({ action: 'charge_membership', usuario_id: userId, plan_id: planId, request_id: request, monto: lockedAmount.current, grupo_id: group?.id, notas: notes })); }
    catch (failure) {
      setError(failure instanceof Error ? failure.message : 'No se pudo confirmar el envío. Consulta antes de volver a cobrar.');
      if (failure && typeof failure === 'object' && 'definitive' in failure && failure.definitive) { setBusy(false); localStorage.removeItem(key); lockedAmount.current = null; }
    } finally { inFlight.current = false; }
  };
  const cancel = async () => {
    if (!order || inFlight.current) return;
    inFlight.current = true;
    try { apply(await invoke({ action: 'cancel', order_id: order.id })); setError(''); }
    catch (failure) { setError(failure instanceof Error ? failure.message : 'No se pudo cancelar. Consulta su estado.'); }
    finally { inFlight.current = false; }
  };
  return { group, order, busy, ready, error, charge, refresh, recover, cancel, activeRowId: activeRow.current };
}
